import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useSessionState } from './SessionContext';
import {
  createStaffUser,
  deleteStaffUser,
  fetchAllStaffUsers,
  fetchStaffPerformance,
  updateStaffUser,
} from '@/lib/api';
import type { HomeStaffPerformanceRecord, StaffApiUser, StaffMember } from '@/lib/types';

type StaffDraft = {
  name: string;
  email: string;
  phoneNumber: string;
  password: string;
};

type StaffUpdate = Partial<StaffDraft>;

type StaffDirectoryContextValue = {
  isReady: boolean;
  staffMembers: StaffMember[];
  addStaff: (draft: StaffDraft) => Promise<StaffMember>;
  updateStaff: (staffId: StaffMember['id'], updates: StaffUpdate) => Promise<StaffMember | null>;
  deleteStaff: (staffId: StaffMember['id']) => Promise<boolean>;
  refreshStaff: () => Promise<void>;
};

const StaffDirectoryContext = createContext<StaffDirectoryContextValue | null>(null);

function buildColorFromIdentifier(identifier: string, index: number) {
  const seed = Array.from(identifier).reduce((sum, character) => sum + character.charCodeAt(0), 0);
  const palette = ['#27272A', '#52525B', '#71717A', '#A1A1AA', '#3F3F46', '#D4D4D8'];
  return palette[(seed + index) % palette.length];
}

function normalizePerformanceMap(records: HomeStaffPerformanceRecord[]) {
  return new Map(records.map((record) => [record.id, record] as const));
}

function toStaffMember(
  user: StaffApiUser,
  index: number,
  performanceMap: Map<string, HomeStaffPerformanceRecord>,
  passwordOverride: string | null = null
): StaffMember {
  const performance = performanceMap.get(user.id);

  return {
    id: user.id,
    username: user.username,
    name: user.name,
    email: user.email,
    phoneNumber: user.phoneNumber,
    password: passwordOverride ?? '',
    role: user.role === 'staff' ? 'Staff' : 'Admin',
    color: performance?.color ?? buildColorFromIdentifier(user.id, index),
    specialty: user.role === 'staff' ? 'Floor staff' : 'Admin',
    bookings: performance?.bookings ?? 0,
    revenue: performance?.revenue ?? 0,
    retention: performance?.retention ?? 0,
    onTime: performance?.on_time ?? 0,
    rating: performance?.rating ?? 0,
  };
}

function buildLocalStaffMembers(): StaffMember[] {
  return [];
}

function buildStaffMembers(
  users: StaffApiUser[],
  performanceRecords: HomeStaffPerformanceRecord[],
  passwordCache: Map<string, string>
) {
  const performanceMap = normalizePerformanceMap(performanceRecords);

  return users.map((user, index) => {
    const cachedPassword = passwordCache.get(user.id) ?? null;
    return toStaffMember(user, index, performanceMap, cachedPassword);
  });
}

function sanitizeStaffDraft(draft: StaffDraft) {
  return {
    name: draft.name.trim(),
    email: draft.email.trim().toLowerCase(),
    phoneNumber: draft.phoneNumber.trim(),
    password: draft.password.trim(),
  };
}

export function StaffDirectoryProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useSessionState();
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>(buildLocalStaffMembers());
  const [isReady, setIsReady] = useState(false);
  const passwordCacheRef = useRef<Map<string, string>>(new Map());
  const performanceCacheRef = useRef<Map<string, HomeStaffPerformanceRecord>>(new Map());

  const restoreLocalStaffMembers = useCallback(() => {
    setStaffMembers(buildLocalStaffMembers());
  }, []);

  const refreshStaff = useCallback(async () => {
    if (!isAuthenticated) {
      restoreLocalStaffMembers();
      return;
    }

    try {
      const [users, performanceRecords] = await Promise.all([
        fetchAllStaffUsers({ role: 'staff' }),
        fetchStaffPerformance().catch(() => []),
      ]);

      performanceCacheRef.current = normalizePerformanceMap(performanceRecords);
      setStaffMembers(buildStaffMembers(users, performanceRecords, passwordCacheRef.current));
    } catch {
      // Keep the last known staff list if the network is unavailable.
    }
  }, [isAuthenticated, restoreLocalStaffMembers]);

  useEffect(() => {
    let active = true;

    (async () => {
      if (!isAuthenticated) {
        restoreLocalStaffMembers();

        if (active) {
          setIsReady(true);
        }

        return;
      }

      await refreshStaff();

      if (active) {
        setIsReady(true);
      }
    })();

    return () => {
      active = false;
    };
  }, [isAuthenticated, refreshStaff, restoreLocalStaffMembers]);

  const addStaff = useCallback(
    async (draft: StaffDraft) => {
      const nextDraft = sanitizeStaffDraft(draft);
      const createdUser = await createStaffUser({
        ...nextDraft,
        role: 'staff',
      });

      if (nextDraft.password) {
        passwordCacheRef.current.set(createdUser.id, nextDraft.password);
      }

      const nextStaff = toStaffMember(
        createdUser,
        staffMembers.length,
        performanceCacheRef.current,
        nextDraft.password
      );

      await refreshStaff();

      return nextStaff;
    },
    [refreshStaff, staffMembers.length]
  );

  const updateStaff = useCallback(
    async (staffId: StaffMember['id'], updates: StaffUpdate) => {
      const currentStaff = staffMembers.find((staff) => staff.id === staffId) ?? null;
      const nextUpdates: Record<string, unknown> = {};

      if (typeof updates.name === 'string' && updates.name.trim()) {
        nextUpdates.name = updates.name.trim();
      }

      if (typeof updates.email === 'string' && updates.email.trim()) {
        nextUpdates.email = updates.email.trim().toLowerCase();
      }

      if (typeof updates.phoneNumber === 'string' && updates.phoneNumber.trim()) {
        nextUpdates.phoneNumber = updates.phoneNumber.trim();
      }

      if (typeof updates.password === 'string' && updates.password.trim()) {
        nextUpdates.password = updates.password.trim();
        passwordCacheRef.current.set(staffId, updates.password.trim());
      }

      const updatedUser = await updateStaffUser(staffId, nextUpdates);

      const nextStaff = toStaffMember(
        updatedUser,
        Math.max(0, staffMembers.findIndex((staff) => staff.id === staffId)),
        performanceCacheRef.current,
        passwordCacheRef.current.get(staffId) ?? currentStaff?.password ?? null
      );

      await refreshStaff();

      return nextStaff;
    },
    [refreshStaff, staffMembers]
  );

  const deleteStaff = useCallback(
    async (staffId: StaffMember['id']) => {
      const removed = await deleteStaffUser(staffId);

      if (removed.deleted) {
        passwordCacheRef.current.delete(staffId);
        await refreshStaff();
      }

      return Boolean(removed.deleted);
    },
    [refreshStaff]
  );

  const value = useMemo(
    () => ({
      isReady,
      staffMembers,
      addStaff,
      updateStaff,
      deleteStaff,
      refreshStaff,
    }),
    [addStaff, deleteStaff, isReady, refreshStaff, staffMembers, updateStaff]
  );

  return <StaffDirectoryContext.Provider value={value}>{children}</StaffDirectoryContext.Provider>;
}

export function useStaffDirectoryState() {
  const context = useContext(StaffDirectoryContext);

  if (!context) {
    throw new Error('useStaffDirectoryState must be used within a StaffDirectoryProvider');
  }

  return context;
}

export type { StaffDraft, StaffUpdate };
